/* eslint-disable max-lines */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Image from '@base/Image';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import CardCarousel from '@cont/PreSubscribed/CardCarousel';
import { getImage } from '@utils/imageHelper';
import { emptyObject } from '@constants/empty';
import isEmptyObject from '@utils/isEmptyObject';
import { segment } from '@actions/analytics';
import { ACHIEVERS_CAROUSEL_CARD_CLICKED } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import H2 from '@base/H2/H2';
import H3 from '@base/H3/H3';
import H4 from '@base/H4/H4';
import Button from '@base/Button/Button';
import { useIsMobile } from '@stores/AppCommonData';

const Color_TAN_Dark = 'var(--color-i-brown-2)';
const Color_TAN_Light = 'var(--color-i-orange-5)';
const Color_TAN_Ultra_Light = 'var(--color-i-orange-6)';

const SecWrapper = styled.section`
  position: relative;
  margin: auto;
  margin-top: 195px;
  box-shadow: ${boxShadowGenerator()};
  border-radius: ${Spacings.SPACING_2B};
  width: 1136px;
  height: 610px;
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
  justify-content: center;
  background-color: var(--color-base-1);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url(${getImage('achieversCard/web_mainbg.svg')});
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_22B};
    width: 728px;
    padding-right: ${Spacings.SPACING_0B} !important;
  }
  ${mediaQuery.uptoMobile} {
    height: unset;
    width: 100%;
    background-image: unset;
    background-color: var(--color-i-orange-7);
    flex-direction: column;
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
    margin: ${Spacings.SPACING_14B} 0px 0px;
  }
`;

const StarsImageWrapper = styled.div`
  position: absolute;
  width: ${Spacings.SPACING_9B};
  height: ${Spacings.SPACING_9B};
  right: ${Spacings.SPACING_4B};
  top: ${Spacings.SPACING_4B};
`;

const HeadingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    text-align: left;
    align-items: baseline;
  }
`;

const WrapperHeading = styled(H2)`
  color: ${Color_TAN_Dark};
  ${mediaQuery.uptoMobile} {
    width: 50%;
  }
`;

const WrapperDescription = styled.div`
  color: ${Color_TAN_Dark};
  margin-top: ${Spacings.SPACING_3B};
  font-size: ${Fonts.H5};
  width: 75%;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_2B};
    width: 95%;
  }
`;

const CarouselSection = styled.div`
  margin-top: ${({ $isCommonCta }) =>
    $isCommonCta ? `${Spacings.SPACING_8B}` : `${Spacings.SPACING_13B}`};
  display: flex;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
  }
  & > div:first-child {
    ${mediaQuery.uptoMobile} {
      width: 100vw;
      margin-bottom: -${Spacings.SPACING_12B};
      margin-left: -${Spacings.SPACING_4B};
    }
  }
  ${mediaQuery.uptoTablet} {
    width: 100%;
    overflow: hidden;
  }
  & .card-carosel-container {
    ${mediaQuery.uptoTablet} {
      padding-bottom: 3.5rem;
    }
    ${mediaQuery.aboveTablet} {
      margin-left: ${Spacings.SPACING_15B};
    }
  }
`;

const CardWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 472px;
  height: 328px;
  background-color: ${Color_TAN_Ultra_Light};
  border-radius: ${Spacings.SPACING_3B};
  display: flex;
  justify-content: center;
  margin-right: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoTablet} {
    margin-right: ${Spacings.SPACING_3B};
    width: 320px;
    height: 320px;
  }
`;

const CardContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  z-index: 2;
`;

const CardWrapperBackground = styled(Image)`
  background: transparent;
  z-index: 1;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  object-fit: cover;
  width: 100%;
  height: 100%;
`;

const ProfileIcon = styled.div`
  position: relative;
  border: ${Spacings.SPACING_2} solid ${Color_TAN_Light};
  border-radius: 50%;
`;

const AvatarWrapper = styled(ProfileIcon)`
  width: ${Spacings.SPACING_21B};
  height: ${Spacings.SPACING_21B};
`;

const RankTag = styled.div`
  width: fit-content;
  color: ${Color_TAN_Dark};
  background: ${Color_TAN_Ultra_Light};
  border-radius: ${Spacings.SPACING_10B};
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_10};
  font-size: ${Fonts.ULTRA_SMALL};
  font-weight: ${Fonts.BOLD};
  margin-top: ${Spacings.SPACING_3B};
  background-image: url(${getImage('achieversCard/air_pill_large.svg')});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const Name = styled(H3)`
  margin-top: ${Spacings.SPACING_2B};
`;

const Achievement = styled.div`
  color: ${Color_TAN_Dark};
  font-size: ${Fonts.SMALL};
  width: 60%;
  margin-top: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoMobile} {
    width: 80%;
  }
`;

const CtaButton = styled(Button)`
  background: var(--color-i-brown-2);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_9B};
  margin-top: ${Spacings.SPACING_5B};
  color: var(--color-i-white);
  height: unset;
  min-height: unset;
  font-size: ${Fonts.SMALL};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'fit-content')};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_7B};
    border-radius: ${Spacings.SPACING_1B};
    font-size: ${Fonts.ULTRA_SMALL};
  }
`;

const QuotesIconWrapper = styled.div`
  position: relative;
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};
`;

const TestimonialIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${Spacings.SPACING_2B};
  width: 65%;
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: ${Spacings.SPACING_2};
  }
  &::before {
    background: linear-gradient(to left, ${Color_TAN_Light}, transparent);
  }
  &::after {
    background: linear-gradient(to right, ${Color_TAN_Light}, transparent);
  }
`;

const TestimonialText = styled(H4)`
  color: ${Color_TAN_Dark};
  font-weight: ${Fonts.BOLD};
  margin: ${Spacings.SPACING_6B} ${Spacings.SPACING_0B};
  width: 75%;
`;

const TestimonialIconInner = styled.div`
  padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
`;

const TestimonialIcon = styled(ProfileIcon)`
  position: relative;
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};
`;

const TestimonialName = styled.div`
  color: ${Color_TAN_Dark};
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.SMALL};
`;

const OtherAchieversWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 90%;
`;

const AchieversProfileWrapper = styled.div`
  display: flex;
  column-gap: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    column-gap: ${Spacings.SPACING_1B};
  }
`;

const AchieversProfile = styled.div`
  width: 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${({ $isCenter }) => $isCenter && 'transform: translateY(-6px);'}
`;

const OtherAchieversImage = styled(ProfileIcon)`
  width: ${Spacings.SPACING_15B};
  height: ${Spacings.SPACING_15B};
  ${({ $isCenter }) =>
    $isCenter &&
    `width: ${Spacings.SPACING_18B};height: ${Spacings.SPACING_18B};`}
  ${mediaQuery.uptoMobile} {
    width: ${Spacings.SPACING_12B};
    height: ${Spacings.SPACING_12B};
    ${({ $isCenter }) =>
      $isCenter &&
      `width: ${Spacings.SPACING_15B};height: ${Spacings.SPACING_15B};`}
  }
`;

const OtherAchieversRankTag = styled(RankTag)`
  margin: ${Spacings.SPACING_2B} ${Spacings.SPACING_0B};
  background-image: url(${getImage('achieversCard/air_pill_small.svg')});
`;

const OtherAchieversAchievement = styled(Achievement)`
  margin-top: ${Spacings.SPACING_5B};
  width: 70%;
`;

const StatisticHeading = styled.div`
  color: ${Color_TAN_Dark};
  font-size: ${Fonts.LARGE};
  font-weight: ${Fonts.BOLD};
  margin-bottom: ${Spacings.SPACING_5B};
`;
const StatisticCount = styled(Name)`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_1B};
`;

const StatisticLabel = styled.div`
  color: ${Color_TAN_Dark};
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.BOLD};
  margin-bottom: ${Spacings.SPACING_2B};
`;

const CommonCtaButton = styled(CtaButton)`
  ${mediaQuery.uptoMobile} {
    border-radius: ${Spacings.SPACING_2B};
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_9B};
    font-size: ${Fonts.SMALL};
    width: 90%;
    margin-left: -${Spacings.SPACING_4B};
  }
`;

const handleCardClick = (ctaLink, segmentData) => {
  segment.track(ACHIEVERS_CAROUSEL_CARD_CLICKED, {
    ...segmentData
  });
  window.open(ctaLink, '_blank');
};

const TopRankersCard = ({
  isMobile,
  type,
  data,
  extraInfo,
  goalSegmentData
}) => {
  const achieverDetails = data?.[0] ?? emptyObject;
  const { name, achievement, icon, rankTag } = achieverDetails;
  const { ctaLabel, ctaLink } = extraInfo ? extraInfo.ctaDetails : {};
  const imgSrc = isMobile ? 'mobile' : 'web';
  const src = `achieversCard/${imgSrc}_${type}.svg`;
  if (!isEmptyObject(achieverDetails)) {
    return (
      <CardWrapper>
        <CardWrapperBackground
          src={src}
          layout="fill"
          width={472}
          height={328}
        />
        <CardContent>
          <AvatarWrapper>
            <Image src={icon} layout="fill" alt="" />
          </AvatarWrapper>
          <RankTag>{rankTag}</RankTag>
          <Name color={Color_TAN_Dark}>{name}</Name>
          <Achievement>{achievement}</Achievement>
          {ctaLabel && (
            <CtaButton
              onClick={() =>
                handleCardClick(ctaLink, {
                  ...goalSegmentData,
                  card_type: type
                })
              }
              label={ctaLabel}
            />
          )}
        </CardContent>
      </CardWrapper>
    );
  }
};

const TestimonialCard = ({ isMobile, type, data }) => {
  const testimonialDetails = data?.[0] ?? emptyObject;
  const { testimonial, topRankerUserInfo = {} } = testimonialDetails;
  const { icon, name } = topRankerUserInfo;
  const imgSrc = isMobile ? 'mobile' : 'web';
  const src = `achieversCard/${imgSrc}_${type}.svg`;

  if (testimonial) {
    return (
      <CardWrapper>
        <CardWrapperBackground
          src={src}
          layout="fill"
          width={472}
          height={328}
        />
        <CardContent>
          <QuotesIconWrapper>
            <Image src="achieversCard/quotes.svg" alt="" layout="fill" />
          </QuotesIconWrapper>
          <TestimonialText>{testimonial}</TestimonialText>
          <TestimonialIconWrapper>
            <TestimonialIconInner>
              <TestimonialIcon>
                <Image src={icon} layout="fill" alt="" />
              </TestimonialIcon>
            </TestimonialIconInner>
          </TestimonialIconWrapper>
          <TestimonialName>{name}</TestimonialName>
        </CardContent>
      </CardWrapper>
    );
  }
};

const OtherAchievers = ({
  isMobile,
  type,
  data,
  extraInfo,
  goalSegmentData
}) => {
  const achieverDetails = data ?? [];
  const { subHeader, ctaDetails = {} } = extraInfo ?? emptyObject;
  const { ctaLabel, ctaLink } = ctaDetails;
  const imgSrc = isMobile ? 'mobile' : 'web';
  const src = `achieversCard/${imgSrc}_${type}.svg`;

  if (achieverDetails?.length) {
    return (
      <CardWrapper>
        <CardWrapperBackground
          src={src}
          layout="fill"
          width={472}
          height={328}
        />
        <CardContent>
          <OtherAchieversWrapper>
            <AchieversProfileWrapper>
              {achieverDetails.map((achiever, index) => (
                <AchieversProfile
                  key={achiever.rankTag}
                  $isCenter={index === 1}
                >
                  <OtherAchieversImage $isCenter={index === 1}>
                    <Image src={achiever.icon} alt="" layout="fill" />
                  </OtherAchieversImage>
                  <OtherAchieversRankTag>
                    {achiever.rankTag}
                  </OtherAchieversRankTag>
                  <TestimonialName>{achiever.name}</TestimonialName>
                </AchieversProfile>
              ))}
            </AchieversProfileWrapper>
            <OtherAchieversAchievement>{subHeader}</OtherAchieversAchievement>
            {ctaLabel && (
              <CtaButton
                onClick={() =>
                  handleCardClick(ctaLink, {
                    ...goalSegmentData,
                    card_type: type
                  })
                }
                label={ctaLabel}
              />
            )}
          </OtherAchieversWrapper>
        </CardContent>
      </CardWrapper>
    );
  }
};

const CumulativeStatistics = ({ isMobile, type, data, extraInfo }) => {
  const Statistics = data ?? [];
  const { header } = extraInfo ?? emptyObject;
  const imgSrc = isMobile ? 'mobile' : 'web';
  const src = `achieversCard/${imgSrc}_${type}.svg`;
  if (Statistics?.length) {
    return (
      <CardWrapper>
        <CardWrapperBackground
          src={src}
          layout="fill"
          width={472}
          height={328}
        />
        <CardContent>
          <OtherAchieversWrapper>
            <StatisticHeading>{header}</StatisticHeading>
            <AchieversProfileWrapper>
              {Statistics.map((statistic, index) => (
                <AchieversProfile key={String(index)}>
                  <Image
                    src={`achieversCard/trophy${index + 1}.svg`}
                    width={isMobile ? 52 : 70}
                    height={isMobile ? 52 : 70}
                    alt=""
                  />
                  <StatisticCount color={Color_TAN_Dark}>
                    {statistic.noOfAchivers}
                  </StatisticCount>
                  <StatisticLabel>{statistic.label}</StatisticLabel>
                  <TestimonialName>
                    {statistic.achievementsRangeTag}
                  </TestimonialName>
                </AchieversProfile>
              ))}
            </AchieversProfileWrapper>
          </OtherAchieversWrapper>
        </CardContent>
      </CardWrapper>
    );
  }
};

const CardsComponents = {
  top_ranker_info: TopRankersCard,
  testimonial: TestimonialCard,
  other_achievers: OtherAchievers,
  cumulative_statistics: CumulativeStatistics
};

const renderCardComponents = (isMobile, cards, goalSegmentData) => {
  return cards.map((card, index) => {
    const { type, data, extraInfo } = card;
    const Component = CardsComponents[type];
    if (Component) {
      return (
        <Component
          key={String(index)}
          type={type}
          data={data}
          extraInfo={extraInfo}
          isMobile={isMobile}
          goalSegmentData={goalSegmentData}
        />
      );
    }
    return null;
  });
};

const AchieversCard = ({ block, goalUID }) => {
  const {
    data = [],
    extraBlockInfo: { header, description, ctaDetails = {} },
    type
  } = block;
  const { ctaLabel, ctaLink } = ctaDetails;
  const isMobile = useIsMobile();
  const goalSegmentData = useGoalEventProps({ goalUID });
  if (data?.length) {
    return (
      <SecWrapper>
        {isMobile && (
          <StarsImageWrapper>
            <Image src="achieversCard/stars.svg" alt="" layout="fill" />
          </StarsImageWrapper>
        )}
        <HeadingSection>
          {header && <WrapperHeading>{header}</WrapperHeading>}
          {description && (
            <WrapperDescription>{description}</WrapperDescription>
          )}
          {!isMobile && ctaLabel && (
            <CtaButton
              onClick={() =>
                handleCardClick(ctaLink, {
                  ...goalSegmentData,
                  card_type: type
                })
              }
              label={ctaLabel}
            />
          )}
        </HeadingSection>
        <CarouselSection $isCommonCta={!!ctaLabel}>
          <CardCarousel step={2} type="dummy" rightShadowDiv={false}>
            {renderCardComponents(isMobile, data, goalSegmentData)}
          </CardCarousel>
        </CarouselSection>
        {isMobile && ctaLabel && (
          <CommonCtaButton
            $fullWidth
            onClick={() =>
              handleCardClick(ctaLink, {
                ...goalSegmentData,
                card_type: type
              })
            }
            label={ctaLabel}
          />
        )}
      </SecWrapper>
    );
  }
};

AchieversCard.propTypes = {
  block: PropTypes.objectOf(PropTypes.any).isRequired,
  goalUID: PropTypes.string.isRequired
};

export default AchieversCard;
